import { useParams } from 'react-router-dom';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ViewTable from 'app/shared/view-table/view-table';
import ViewTableHeaderColumn from 'app/shared/view-table/view-table-header-column';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import {
  deleteSpecialBuildingPartQuery,
  fetchSpecialBuildingPartsQuery,
  moveSpecialBuildingPartDownQuery,
  moveSpecialBuildingPartUpQuery,
} from 'app/edozvola/cases/special-building-part/service/special-building-part.queries';
import { SpecialBuildingPartView } from 'app/edozvola/cases/special-building-part/model/special-building-part.model';
import { getInvalidRowsForTable } from 'app/shared/util/validation-errors.util';
import { TABLE_QUERY_KEYS } from 'app/config/constants';
import { TooltipWrapper } from 'edozvola-frontend-lib/dist/ui/components/tooltipwrapper';
import { getCaseContext } from 'app/shared/util/context-util';

export const formatNetArea = (value: number) =>
  value === null || value === undefined
    ? ''
    : Number(value).toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SpecialBuildingPartTable = ({ setSpecialBuildingPart, setReadOnly }) => {
  const { id } = useParams<{ id: string }>();
  const [idToDelete, setIdToDelete] = useState();
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const { sidebarItemPair } = getCaseContext();

  const invalidRowIds = getInvalidRowsForTable(TABLE_QUERY_KEYS.SPECIAL_BUILDING_PARTS_TABLE);

  const { mutate: deleteMutation } = deleteSpecialBuildingPartQuery();
  const { mutate: moveUpMutation } = moveSpecialBuildingPartUpQuery();
  const { mutate: moveDownMutation } = moveSpecialBuildingPartDownQuery();

  const openDeleteDialog = idSpecialBuildingPart => {
    setIdToDelete(idSpecialBuildingPart);
    setDeleteModalShown(true);
  };

  const removeSpecialBuildingPart = () => {
    setDeleteModalShown(false);
    deleteMutation(idToDelete);
  };

  const openForm = (idSpecialBuildingPart: number, readOnly: boolean) => {
    setSpecialBuildingPart(idSpecialBuildingPart);
    setReadOnly(readOnly);
  };

  const renderTableRow = (value: SpecialBuildingPartView, rowIndex: number, totalItems: number) => {
    return (
      <tr key={rowIndex} className={invalidRowIds.includes(value.idSpecialBuildingPart) ? 'invalid-table-row' : ''}>
        <td>{value.buildingName}</td>
        <td>{value.ibz}</td>
        <td>{value.buildingPart}</td>
        <td>{value.specialBuildingPart}</td>
        <td>{value.spaceType}</td>
        <td>{value.floor}</td>
        <td>{value.floorType}</td>
        <td className={'text-end'}>{formatNetArea(value.usefulNetArea)}</td>
        <td>{value.roomCount}</td>
        <td>
          <div className={'actions-container'}>
            {sidebarItemPair?.canView && (
              <TooltipWrapper message={'Pregledaj'}>
                <FontAwesomeIcon icon={'eye'} className={'icon-table'} onClick={() => openForm(value.idSpecialBuildingPart, true)} />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canEdit && (
              <TooltipWrapper message={'Uredi'}>
                <FontAwesomeIcon
                  icon={'pen-to-square'}
                  className={'icon-table'}
                  onClick={() => openForm(value.idSpecialBuildingPart, false)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canDelete && (
              <TooltipWrapper message={'Obriši'}>
                <FontAwesomeIcon
                  icon={'trash-alt'}
                  className={'icon-table'}
                  onClick={() => openDeleteDialog(value.idSpecialBuildingPart)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canChangeOrderAscending && (
              <TooltipWrapper message={'Promijeni redoslijed prema gore'}>
                <FontAwesomeIcon
                  icon={'arrow-up'}
                  className={'icon-table'}
                  onClick={() => moveUpMutation(value.idSpecialBuildingPart)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canChangerOrderDescending && (
              <TooltipWrapper message={'Promijeni redoslijed prema dolje'}>
                <FontAwesomeIcon
                  icon={'arrow-down'}
                  className={'icon-table'}
                  onClick={() => moveDownMutation(value.idSpecialBuildingPart)}
                />
              </TooltipWrapper>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <ViewTable
        renderTableRow={renderTableRow}
        noDataMessage={'Nema unesenih posebnih dijelova zgrade'}
        initialPaginationColumn={'ordering'}
        fetchDataQuery={fetchSpecialBuildingPartsQuery}
        fetchDataProps={[id]}
      >
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Zgrada']} width={'15%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Zgrada - IBZ']} width={'10%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Dio zgrade']} width={'10%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Posebni dio zgrade']} width={'12%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Vrsta']} width={'12%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Etaža']} width={'10%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Etaža - razina']} width={'9%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['KNP [m2]']} width={'6%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Broj soba']} width={'7%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Akcije']} width={'9%'} />
      </ViewTable>
      <Modal isOpen={deleteModalShown} size={'lg'} toggle={() => setDeleteModalShown(false)} centered>
        <ModalHeader>
          <span>Brisanje posebnog dijela zgrade</span>
        </ModalHeader>
        <ModalBody>
          <div>Jeste li sigurni da želite obrisati podatke?</div>
        </ModalBody>
        <ModalFooter>
          <Button size={'lg'} label="Odustani" outline onClick={() => setDeleteModalShown(false)} />
          <Button size={'lg'} label="Obriši" color={'danger'} onClick={removeSpecialBuildingPart} />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SpecialBuildingPartTable;
